import type { TripStop } from '../types/trip';

const API_BASE_URL =
  process.env.NEXT_PUBLIC_API_URL ||
  process.env.NEXT_PUBLIC_API_BASE_URL ||
  '';

const resolveUrl = (path: string) =>
  API_BASE_URL ? `${API_BASE_URL}${path}` : path;

type AgentApiMessage = {
  role: string;
  content: string;
  metadata?: Record<string, string>;
};

export type AgentMessageResponse = {
  messages: AgentApiMessage[];
  context?: Record<string, string>;
  handoff?: string | null;
  instructions?: string | null;
};

export async function createAgentSession(context?: Record<string, string>) {
  const response = await fetch(resolveUrl('/api/agents/session'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ context }),
  });

  if (!response.ok) {
    throw new Error(`Failed to create agent session (${response.status})`);
  }

  return (await response.json()) as {
    session_id: string;
    context: Record<string, string>;
  };
}

export async function sendAgentMessage(params: {
  sessionId: string;
  content: string;
  taskType?: string;
  contextUpdates?: Record<string, string>;
  agent?: string;
}): Promise<AgentMessageResponse> {
  const { sessionId, content, taskType, contextUpdates, agent } = params;
  const sanitizedUpdates = contextUpdates
    ? Object.fromEntries(
        Object.entries(contextUpdates)
          .filter(
            ([, value]) =>
              value !== undefined && value !== null && value !== ''
          )
          .map(([key, value]) => [key, String(value)])
      )
    : undefined;

  const response = await fetch(
    resolveUrl(`/api/agents/session/${sessionId}/message`),
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        content,
        task_type: taskType,
        context_updates: sanitizedUpdates,
        agent,
      }),
    }
  );

  if (!response.ok) {
    throw new Error(`Agent request failed (${response.status})`);
  }

  return (await response.json()) as AgentMessageResponse;
}

export function summariseTripForAssistant(stops: TripStop[]): string {
  if (!stops.length) {
    return 'No trip stops yet.';
  }

  return stops
    .map((stop, index) => {
      const coords =
        typeof stop.lat === 'number' && typeof stop.lng === 'number'
          ? `(${stop.lat.toFixed(3)}, ${stop.lng.toFixed(3)})`
          : '(coordinates unknown)';
      const tags =
        stop.tags && stop.tags.length > 0
          ? ` tags: ${stop.tags.join(', ')}`
          : '';
      return `${index + 1}. ${stop.name || 'Unnamed stop'} ${coords}${tags}`;
    })
    .join('\n');
}

export function inferAssistantTaskType(message: string): string {
  const lower = message.toLowerCase();

  if (/(book|availability|reserve|reservation|hold)/.test(lower)) {
    return 'booking_request';
  }

  if (/(event|activity|things to do|schedule|festival|show)/.test(lower)) {
    return 'event_plan';
  }

  if (
    /(route|trip|stop|ladder|itinerary|add|remove|radius|corridor|distance)/.test(
      lower
    )
  ) {
    return 'trip_plan';
  }

  return 'conversation';
}
